/**
 * The judgements the portfolio screen makes, kept apart from the markup so
 * they can be read and tested without rendering anything.
 *
 * Two rules run through all of it. Missing is not zero: a position whose cost
 * basis the indexer never saw has an unknown profit, not a profit of nothing,
 * and it says so with a dash. And a share is only a share of what is known —
 * buckets that exclude the unknowns would add up to a hundred percent and lie
 * about it.
 */

import type { WalletProfile } from '@root/features/services/wallet-profile'

export type Tone = 'quiet' | 'good' | 'bad'

export const signTone = (value: number | undefined): Tone => {
  if (value === undefined || !Number.isFinite(value) || value === 0) {
    return 'quiet'
  }
  return value > 0 ? 'good' : 'bad'
}

export type Bucket = {
  key: string
  label: string
  count: number
  /** Of the whole set, 0…100. Zero when the set is empty. */
  share: number
  tone: Tone
}

const shareOf = (count: number, total: number) =>
  total <= 0 ? 0 : (count / total) * 100

/**
 * How the closed trades turned out, worst to best.
 *
 * The five bands are the server's own, and they are the bands every terminal
 * in this market uses, so a trader reading ours against another is comparing
 * like with like. Order runs upward, because a distribution read left to right
 * should climb.
 */
export const returnBuckets = (
  distribution: WalletProfile.Distribution,
): ReadonlyArray<Bucket> => {
  const rows = [
    { key: 'lossOver50', label: '< -50%', count: distribution.lossOver50 },
    { key: 'loss0To50', label: '-50 – 0%', count: distribution.loss0To50 },
    { key: 'gain0To200', label: '0 – 200%', count: distribution.gain0To200 },
    {
      key: 'gain200To500',
      label: '200 – 500%',
      count: distribution.gain200To500,
    },
    { key: 'gainOver500', label: '> 500%', count: distribution.gainOver500 },
  ]

  const total = rows.reduce((sum, row) => sum + row.count, 0)

  return rows.map((row) => ({
    ...row,
    share: shareOf(row.count, total),
    tone: row.key.startsWith('loss') ? ('bad' as const) : ('good' as const),
  }))
}

/**
 * What size of token this account buys into.
 *
 * Ours, not borrowed: the entry market cap is the one figure that says whether
 * somebody is playing the first minute of a launch or waiting for a floor, and
 * two accounts with the same profit and different answers here are not doing
 * the same job. The unknown bucket is shown rather than dropped, because its
 * size is how much of the rest to trust.
 */
export const entryCapBuckets = (
  caps: WalletProfile.EntryMarketCaps,
): ReadonlyArray<Bucket> => {
  const known = caps.below100k + caps.from100kTo500k + caps.above500k

  return [
    { key: 'below100k', label: '< $100K', count: caps.below100k },
    {
      key: 'from100kTo500k',
      label: '$100K – $500K',
      count: caps.from100kTo500k,
    },
    { key: 'above500k', label: '> $500K', count: caps.above500k },
    { key: 'unknown', label: 'Unknown', count: caps.unknown },
  ].map((row) => ({
    ...row,
    // Against the known ones, so the three real bands read as a split of the
    // trades we can place. The unknown band gets no share of its own.
    share: row.key === 'unknown' ? 0 : shareOf(row.count, known),
    tone: 'quiet' as const,
  }))
}

export type PositionReading = {
  /** Something is still held. */
  open: boolean
  /** Dollars, or nothing when the cost basis is unknown. */
  pnl: number | undefined
  /** Against what was spent, 0…∞. Nothing when either side is unknown. */
  pnlPercent: number | undefined
  tone: Tone
}

/**
 * What one token came to.
 *
 * `costBasisKnown === false` means the indexer never saw the buys — the token
 * arrived by transfer, or from before the range it holds. The profit is then
 * genuinely unknown, and a zero in that slot would read as break-even.
 */
export const positionReading = (
  position: WalletProfile.Position,
): PositionReading => {
  const open = position.remainingTokens > 0

  if (!position.costBasisKnown) {
    return { open, pnl: undefined, pnlPercent: undefined, tone: 'quiet' }
  }

  const pnl = position.pnl
  const pnlPercent =
    position.spentUsd > 0 ? (pnl / position.spentUsd) * 100 : undefined

  return { open, pnl, pnlPercent, tone: signTone(pnl) }
}

/**
 * How long a hold lasted, in the two largest units that fit.
 *
 * Seconds beside days would be noise; one unit alone rounds a day and a half
 * to a day. Nothing comes back for nothing held.
 */
export const holdFor = (seconds: number | null | undefined): string => {
  if (seconds === undefined || seconds === null || !Number.isFinite(seconds)) {
    return '—'
  }
  const whole = Math.floor(Math.abs(seconds))
  if (whole < 1) {
    return '—'
  }

  const days = Math.floor(whole / 86_400)
  const hours = Math.floor((whole % 86_400) / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const rest = whole % 60

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  if (minutes > 0) {
    return rest > 0 ? `${minutes}m ${rest}s` : `${minutes}m`
  }
  return `${rest}s`
}

export type Holding = {
  address: string
  ticker: string
  title: string
  icon: string | undefined
  amount: number
  valueUsd: number
  priceUsd: number
  /** How many wallets hold it. */
  wallets: number
  /** Of everything held, 0…100. */
  share: number
}

/** One wallet's line in a holding, before the wallets are merged. */
export type HoldingPart = Omit<Holding, 'wallets' | 'share'>

/**
 * Everything held, across every wallet, as one list.
 *
 * The wallet table answers "where is my money"; this answers "what is it in",
 * which is the question a trader with six wallets and one token actually has.
 * Nothing in the app merged them before, so this is where it happens: summed
 * by mint, largest value first, with the number of wallets kept because a
 * holding split six ways is a different thing to sell.
 */
export const mergeHoldings = (
  parts: ReadonlyArray<HoldingPart>,
): ReadonlyArray<Holding> => {
  const merged = new Map<string, Holding>()

  for (const part of parts) {
    const found = merged.get(part.address)
    if (found === undefined) {
      merged.set(part.address, { ...part, wallets: 1, share: 0 })
      continue
    }
    merged.set(part.address, {
      ...found,
      amount: found.amount + part.amount,
      valueUsd: found.valueUsd + part.valueUsd,
      // The price is the same asset's price; keep the one we have rather than
      // averaging two readings of one number.
      priceUsd: found.priceUsd || part.priceUsd,
      wallets: found.wallets + 1,
    })
  }

  const rows = [...merged.values()]
  const total = rows.reduce((sum, row) => sum + row.valueUsd, 0)

  return rows
    .map((row) => ({ ...row, share: shareOf(row.valueUsd, total) }))
    .sort((left, right) => right.valueUsd - left.valueUsd)
}

export type CurvePoint = { day: string; value: number }

export type Curve = {
  /** The line itself, in the box's own coordinates. */
  line: string
  /** The same line closed down to the baseline, for the wash beneath it. */
  area: string
  /** Where zero sits, so a curve that went under it reads right. */
  zeroY: number
  first: number
  last: number
  /** Nothing to draw. */
  flat: boolean
}

/**
 * The cumulative realised curve, as an SVG path.
 *
 * Drawn here rather than handed to a chart library because the whole shape is
 * eleven lines of arithmetic, and the library we have costs a megabyte and
 * reads the terminal's own theme tokens — which resolve to the light theme on
 * this screen, the trap that turned the no-logo initial black on black.
 *
 * The baseline is zero when the series crosses it, so a wash under a losing
 * week hangs below the line instead of sitting on the bottom of the box.
 */
export const curveOf = (
  points: ReadonlyArray<CurvePoint>,
  width: number,
  height: number,
  pad = 2,
): Curve => {
  const values = points.map((point) => point.value)
  const first = values.at(0) ?? 0
  const last = values.at(-1) ?? 0

  if (points.length < 2) {
    return {
      line: '',
      area: '',
      zeroY: height / 2,
      first,
      last,
      flat: true,
    }
  }

  const low = Math.min(...values, 0)
  const high = Math.max(...values, 0)
  const span = high - low
  const inner = height - pad * 2

  const y = (value: number) =>
    span <= 0 ? height / 2 : pad + inner - ((value - low) / span) * inner
  const x = (index: number) =>
    points.length === 1
      ? width / 2
      : (index / (points.length - 1)) * (width - pad * 2) + pad

  const line = points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${x(index).toFixed(2)} ${y(point.value).toFixed(2)}`,
    )
    .join(' ')

  const zeroY = y(0)
  const area = `${line} L${x(points.length - 1).toFixed(2)} ${zeroY.toFixed(2)} L${x(0).toFixed(2)} ${zeroY.toFixed(2)} Z`

  return { line, area, zeroY, first, last, flat: span <= 0 }
}

/**
 * Per-day steps out of a cumulative series.
 *
 * The server sends the running total, which is right for a curve and wrong for
 * a calendar: a good Tuesday inside a bad month still shows above the line in
 * a total. The first day has nothing before it, so it stands as it is.
 */
export const dailySteps = (
  points: ReadonlyArray<CurvePoint>,
): ReadonlyArray<CurvePoint> =>
  points.map((point, index) => ({
    day: point.day,
    value:
      index === 0 ? point.value : point.value - (points[index - 1]?.value ?? 0),
  }))

/**
 * Win rate from the closed trades, as a percentage.
 *
 * Nothing closed means no rate — not nought percent, which reads as a trader
 * who loses every time rather than one who has not sold yet.
 */
export const winRateOf = (
  closed: number,
  winning: number,
): number | undefined => (closed <= 0 ? undefined : (winning / closed) * 100)

export type Analysis = {
  /** Everything that moved, bought and sold together. */
  volumeUsd: number
  /** What was paid in, all told. */
  costUsd: number
  /** Per token traded, in and out. */
  avgCostUsd: number
  avgSoldUsd: number
  /** Per position that closed. */
  avgRealisedUsd: number | undefined
  /** What the open positions are worth at the price they trade at now. */
  openValueUsd: number
}

/**
 * The figures our own dashboard has always shown, beside the borrowed ones.
 *
 * Averages are per token traded rather than per transaction, which is the
 * question somebody actually has — what a position costs me and what it comes
 * back as — and it is how the profile screen has always computed them.
 *
 * Fees are not here. The swap fee a pool charges is inside the price each
 * trade went off at and is never recorded apart from it, so the only honest
 * figure would be a nought that means "not known" rather than "none paid".
 */
export const analysisOf = (
  totals: WalletProfile.Totals,
  positions: ReadonlyArray<WalletProfile.Position>,
): Analysis => ({
  volumeUsd: totals.spentUsd + totals.receivedUsd,
  costUsd: totals.spentUsd,
  avgCostUsd:
    totals.tokensTraded === 0 ? 0 : totals.spentUsd / totals.tokensTraded,
  avgSoldUsd:
    totals.tokensTraded === 0 ? 0 : totals.receivedUsd / totals.tokensTraded,
  // Nothing closed is no average, not an average of nothing.
  avgRealisedUsd:
    totals.closedPositions === 0
      ? undefined
      : totals.realizedPnl / totals.closedPositions,
  openValueUsd: positions.reduce(
    (total, position) => total + position.remainingTokens * position.priceUsd,
    0,
  ),
})
